import CustomDropdown from "../../../../components/CustomDropdown";
import { EditUserSchema } from "../../../../components/Helpers";
import Hoc from "../../../../components/HOC";
import { Formik, Form, Field } from "formik";
import React, { useState, useEffect, useRef } from "react";
import {
  requestEdituser,
  requestUserAction,
  requestUserById,
  setResetStateEditUser,
} from "../../../../Redux/UserState/UserActionCreator";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "../../../../components/Loader";
import TextInput from "../../../../components/TextInput";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MdArrowBack } from "react-icons/md";
import { FaMapMarkerAlt } from "react-icons/fa";
import { FiUsers, FiUser } from "react-icons/fi";
import MuiDatePicker from "../../../../components/MuiDatePicker";
import TextArea from "../../../../components/TextArea";
import TextAreaInput from "../../../../components/TextArea";
import zipcodes from 'zipcodes';

const EditEmployeeForm = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const title = "Edit Employee";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editUserReducer = useSelector((state) => state.EditUserReducer);
  const userDetailsReducer = useSelector((state) => state.UserByIdReducer);
  const userByIdReducer = useSelector((state) => state.UserByIdReducer);
  const [isAddressAutoFilled, setIsAddressAutoFilled] = useState(false);
  const countryOptions = [
    { value: "USA", label: "USA" },
    { value: "IN", label: "IN" },
  ];
  const initialValues = userByIdReducer.byIdResponse;
  const handleEditEmployee = (newEmployee) => {
    setIsSubmitting(true);
    dispatch(
      requestEdituser({
        ...newEmployee,
        id: userDetailsReducer?.byIdResponse.id,
      })
    );
  };

  useEffect(() => {
    if (!editUserReducer?.Loading && editUserReducer?.Response) {
      toast.success("User Details Updated Successfully");
      setIsSubmitting(false);
      navigate("/manageemployee");
    }
    if (!editUserReducer?.Loading && editUserReducer?.Error) {
      toast.error("Something went wrong");
      dispatch(setResetStateEditUser());
      setIsSubmitting(false);
    }
  }, [editUserReducer]);

  useEffect(() => {
    dispatch(requestUserById(id));
  }, [id]);

  const handleCountryChange = (selectedOption, setFieldValue) => {
    setFieldValue("country", selectedOption.value);
    // Clear all address fields when country changes
    setFieldValue('city', '');
    setFieldValue('state', '');
    setFieldValue('zip', '');
    setFieldValue('addressLine1', '');
    setFieldValue('addressLine2', '');
    setIsAddressAutoFilled(false);
  };

  const handleZipCodeChange = (e, setFieldValue, values, setFieldError, setFieldTouched) => {
    const zipCode = e.target.value;
    setFieldValue('zip', zipCode);
    setFieldTouched('zip', true, false);
    
    if (values.country === 'USA') {
      if (zipCode.length > 5) {
        setFieldError('zip', 'ZIP code cannot exceed 5 digits');
        return;
      }
      if (zipCode.length > 0 && zipCode.length < 5) {
        setFieldError('zip', 'ZIP code must be 5 digits');
        return;
      }
      if (zipCode.length === 5) {
        const location = zipcodes.lookup(zipCode);
        if (location) {
          setFieldValue('city', location.city);
          setFieldValue('state', location.state);
          setFieldValue('country', 'USA');
          setIsAddressAutoFilled(true);
          setFieldValue('zip', zipCode);
        } else {
          toast.error("Invalid ZIP code of USA");
          setIsAddressAutoFilled(false);
        }
      }
    } else if (values.country === 'IN') {
      if (zipCode.length > 6) {
        setFieldError('zip', 'ZIP code cannot exceed 6 digits');
        return;
      }
      if (zipCode.length > 0 && zipCode.length < 6) {
        setFieldError('zip', 'ZIP code must be 6 digits');
        return;
      }
      if (zipCode.length === 6 && !/^[1-9][0-9]{5}$/.test(zipCode)) {
        toast.error("Invalid ZIP code");
        return;
      }
    }
    setIsAddressAutoFilled(false);
  };

  const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    // Ensure first character is a letter and capitalize it
    const firstChar = str.charAt(0);
    if (!/[a-zA-Z]/.test(firstChar)) {
      // If first character is not a letter, remove it
      return str.slice(1);
    }
    return firstChar.toUpperCase() + str.slice(1);
  };

  const handleNameChange = (e, handleChange, setFieldValue, fieldName) => {
    const capitalizedValue = capitalizeFirstLetter(e.target.value);
    setFieldValue(fieldName, capitalizedValue);
  };

  return (
    <div className=" h-[90vh] overflow-y-scroll w-full p-3">
      <Loader isLoading={userByIdReducer?.byIdLoading} />
      <div className="flex items-center gap-2 ">
        <button
          type="button"
          className="text-primary hover:text-primaryDark pl-2 pt-2"
          onClick={() => navigate("/manageemployee")}
        >
          <MdArrowBack size={24} />
        </button>
        <h2 className="text-xl font-normal text-primary">Edit Employee</h2>
      </div>
      <Formik
        initialValues={initialValues || {}}
        enableReinitialize
        validationSchema={EditUserSchema}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={(values) => {
          setIsSubmitting(true);
          const changedKeys = Object.keys(values).filter(
            (key) => values[key] != initialValues[key]
          );

          const changedValues = {};
          changedKeys.forEach((key) => {
            if (key === "joinDate" || key === "endDate") {
              changedValues[key] = moment(values[key]).format(
                "MM-DD-YYYY"
              );
            } else {
              changedValues[key] = values[key];
            }
          });
          handleEditEmployee(changedValues);
        }}
      >
        {({
          values,
          errors,
          touched,
          resetForm,
          handleChange,
          setFieldValue,
          setFieldError,
          setFieldTouched,
        }) => (
          <Form className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[80vh] w-full no-scrollbar rounded px-4 py-2">
            {/* Account Information Section */}
            <div className="bg-gray-50 rounded-lg md:col-span-3 p-5">
              <h2 className="text-md font-medium text-slate-400 flex items-center gap-2">
                <FiUser size={18} className="text-cyan-600" /> Account
                Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <div className="flex flex-col">
                  <TextInput
                    value={values.userName}
                    name="userName"
                    placeholder="User Name"
                    handleChange={handleChange}
                    legend
                    isDisabled
                  />
                  {errors.userName && touched.userName && (
                    <div className="text-red-500 text-sm mt-1">{errors.userName}</div>
                  )}
                </div>
                <div className="flex flex-col">
                  <TextInput
                    value={values.password}
                    name="password"
                    placeholder="Password"
                    handleChange={handleChange}
                    type="password"
                    legend
                    isDisabled
                  />
                  {errors.password && touched.password && (
                    <div className="text-red-500 text-sm mt-1">{errors.password}</div>
                  )}
                </div>
                <div className="flex flex-col">
                  <TextInput
                    value={values.email}
                    name="email"
                    placeholder="Email"
                    handleChange={handleChange}
                    legend
                    // isDisabled
                  />
                  {errors.email && touched.email && (
                    <div className="text-red-500 text-sm mt-1">{errors.email}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="bg-gray-50 rounded-lg md:col-span-3 p-5">
              <h2 className="text-md font-medium text-slate-400 flex items-center gap-2">
                <FiUsers size={18} className="text-cyan-600" /> Personal
                Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <div className="flex flex-col">
                  <TextInput
                    value={values.firstName}
                    name="firstName"
                    placeholder="First Name"
                    handleChange={(e) => {
                      handleNameChange(e, handleChange, setFieldValue, 'firstName');
                    }}
                    legend
                    //isDisabled
                  />
                  {errors.firstName && touched.firstName && (
                    <div className="text-red-500 text-sm mt-1">{errors.firstName}</div>
                  )}
                </div>
                <div className="flex flex-col">
                  <TextInput
                    value={values.lastName}
                    name="lastName"
                    placeholder="Last Name"
                    handleChange={(e) => {
                      handleNameChange(e, handleChange, setFieldValue, 'lastName');
                    }}
                    legend
                    //isDisabled
                  />
                  {errors.lastName && touched.lastName && (
                    <div className="text-red-500 text-sm mt-1">{errors.lastName}</div>
                  )}
                </div>
                <div className="flex flex-col">
                  <TextInput
                    value={values.phone}
                    name="phone"
                    placeholder="Phone Number"
                    type="tel"
                    legend
                    handleChange={handleChange}
                  />
                  {errors.phone && touched.phone && (
                    <div className="text-red-500 text-sm mt-1">{errors.phone}</div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="flex flex-col">
                  <MuiDatePicker
                    label="Start Date"
                    value={values?.joinDate ? moment(values?.joinDate) : null}
                    onChange={(date) => {
                      setFieldValue(
                        "joinDate",
                        moment(date).format("MM-DD-YYYY")
                      );
                    }}
                    disabled
                    className="w-full h-10"
                    maxDate={moment()}
                  />
                  {errors.joinDate && touched.joinDate && (
                    <div className="text-red-500 text-sm mt-1">{errors.joinDate}</div>
                  )}
                </div>

                <div className="flex flex-col">
                  <MuiDatePicker
                    label="End Date"
                    value={values?.endDate ? moment(values?.endDate) : null}
                    onChange={(date) => {
                      setFieldValue(
                        "endDate",
                        moment(date).format("MM-DD-YYYY")
                      );
                    }}
                    className="w-full h-10"
                    minDate={moment()}
                  />
                  {errors.endDate && touched.endDate && (
                    <div className="text-red-500 text-sm mt-1">{errors.endDate}</div>
                  )}
                </div>
                <div className="flex flex-col">
                  <MuiDatePicker
                    label="Date of Birth"
                    value={
                      values?.dateOfBirth ? moment(values?.dateOfBirth) : null
                    }
                    onChange={(date) => {
                      setFieldValue(
                        "dateOfBirth",
                        moment(date).format("MM-DD-YYYY")
                      );
                    }}
                    className="w-full h-10"
                    maxDate={moment().subtract(18,'years')}
                  />
                  {errors.dateOfBirth && touched.dateOfBirth && (
                    <div className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</div>
                  )}
                </div>
              </div>

              <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                <div className="flex flex-col">
                  <div className="flex items-center space-x-4">
                    <label className="text-gray-700 text-sm cursor-default">
                      Gender:
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2">
                        <Field
                          type="radio"
                          name="gender"
                          value="M"
                          id="gender-male"
                          disabled
                        />
                        <span className="text-sm">Male</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <Field
                          type="radio"
                          name="gender"
                          value="F"
                          id="gender-female"
                          disabled
                        />
                        <span className="text-sm">Female</span>
                      </label>
                    </div>
                  </div>
                  {errors.gender && touched.gender && (
                    <div className="text-red-500 text-sm mt-1">{errors.gender}</div>
                  )}
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center space-x-4">
                    <label className="text-gray-700 text-sm cursor-default">
                      Status:
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2">
                        <Field
                          type="radio"
                          name="stsCd"
                          value="A"
                          checked={values.stsCd === "A"}
                        />
                        <span className="text-sm">Active</span>
                      </label>

                      <label className="flex items-center space-x-2">
                        <Field
                          type="radio"
                          name="stsCd"
                          value="D"
                          checked={values.stsCd === "D"}
                        />
                        <span className="text-sm">Deactive</span>
                      </label>
                    </div>
                  </div>
                  {errors.stsCd && touched.stsCd && (
                    <div className="text-red-500 text-sm mt-1">{errors.stsCd}</div>
                  )}
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center space-x-4">
                    <label className="text-gray-700 text-sm cursor-default">
                      Account Type:
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2">
                        <Field
                          type="radio"
                          name="acctType"
                          value="USER"
                          checked={values.acctType === "USER"}
                          disabled
                        />
                        <span className="text-sm">User</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <Field
                          type="radio"
                          name="acctType"
                          value="ADMIN"
                          checked={values.acctType === "ADMIN"}
                          disabled
                        />
                        <span className="text-sm">Admin</span>
                      </label>
                    </div>
                  </div>
                  {errors.acctType && touched.acctType && (
                    <div className="text-red-500 text-sm mt-1">{errors.acctType}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div className="bg-gray-50 p-5 rounded-lg md:col-span-3">
              <h2 className="text-md font-semibold text-slate-400 flex items-center gap-2">
                <FaMapMarkerAlt size={18} className="text-cyan-600" /> Address
                Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <div className="flex flex-col">
                  <TextInput
                    value={values.addressLine1}
                    name="addressLine1"
                    placeholder="Address"
                    handleChange={handleChange}
                    legend
                  />
                  {errors.addressLine1 && touched.addressLine1 && (
                    <div className="text-red-500 text-sm mt-1">{errors.addressLine1}</div>
                  )}
                </div>
                <div className="flex flex-col">
                  <TextInput
                    value={values.addressLine2}
                    name="addressLine2"
                    placeholder="Address"
                    handleChange={handleChange}
                    legend
                  />
                </div>
                <div className="flex flex-col">
                  <TextInput
                    value={values.city}
                    name="city"
                    placeholder="City"
                    handleChange={handleChange}
                    legend
                    isDisabled={values.country === 'USA'}
                  />
                  {errors.city && touched.city && (
                    <div className="text-red-500 text-sm mt-1">{errors.city}</div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="flex flex-col">
                  <TextInput
                    value={values.state}
                    name="state"
                    placeholder="State"
                    handleChange={handleChange}
                    legend
                    isDisabled={values.country === 'USA'}
                  />
                  {errors.state && touched.state && (
                    <div className="text-red-500 text-sm mt-1">{errors.state}</div>
                  )}
                </div>
                <div className="flex flex-col">
                  <CustomDropdown
                    value={countryOptions.find(
                      (user) => user?.value === values?.country
                    )}
                    name="country"
                    handleChange={(selectedOption) =>
                      handleCountryChange(selectedOption, setFieldValue)
                    }
                    placeholder="Select Country"
                    options={countryOptions}
                  />
                  {errors.country && touched.country && (
                    <div className="text-red-500 text-sm mt-1">{errors.country}</div>
                  )}
                </div>
                <div className="flex flex-col">
                  <TextInput
                    value={values.zip}
                    name="zip"
                    placeholder="Zip Code"
                    handleChange={(e) => {
                      const zipCode = e.target.value;
                      if (values.country === 'USA' && zipCode.length > 5) {
                        setFieldError('zip', 'ZIP code cannot exceed 5 digits');
                      } else if (values.country === 'IN' && zipCode.length > 6) {
                        setFieldError('zip', 'ZIP code cannot exceed 6 digits');
                      }
                      handleChange(e);
                      handleZipCodeChange(e, setFieldValue, values, setFieldError, setFieldTouched);
                    }}
                    legend
                  />
                  {errors.zip && touched.zip && (
                    <div className="text-red-500 text-sm mt-1">{errors.zip}</div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2 items-start">
                <div className="md:col-span-2 flex flex-col">
                  <TextAreaInput
                    name="backgroundCheck"
                    value={values.backgroundCheck}
                    handleChange={handleChange}
                    placeholder="Background Check"
                    legend
                  />
                  {errors.backgroundCheck && touched.backgroundCheck && (
                    <div className="text-red-500 text-sm mt-1">{errors.backgroundCheck}</div>
                  )}
                </div>

                <div className="flex justify-end items-start space-x-4">
                  <button
                    type="button"
                    className="bg-gray-300 text-black rounded-md hover:bg-gray-400 w-40 px-4 py-4 text-sm"
                    onClick={() => navigate("/manageemployee")}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className={`bg-primary text-white rounded-md w-40 px-4 py-4 text-sm ${
                      isSubmitting
                        ? "bg-blue-300 cursor-not-allowed opacity-50"
                        : "hover:bg-primaryDark"
                    }`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "..." : "Submit"}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Hoc(EditEmployeeForm);
